/**
 * Article Table of Contents — Minimalist Timeline
 * H2-only, FAQ-style dots + progress fill line, scroll spy, anchor scroll
 */

class ArticleToC {
    constructor() {
        this.articleContent = document.querySelector('.article-content-main .rte');
        this.tocContainer = document.querySelector('.article-toc__list');
        this.sidebar = document.querySelector('.article-toc-sidebar');
        this.headings = [];
        this.dots = [];
        this.progressLine = null;
        this.timelineContainer = null;
        this.isScrolling = false;
        this.lastScrollY = window.pageYOffset;
        this.ticking = false;

        if (this.articleContent && this.tocContainer) {
            this.init();
        }
    }

    init() {
        this.scanHeadings();
        if (this.headings.length > 0) {
            this.generateToC();
            this.positionDots();
            this.setupScrollSpy();
            this.setupDynamicSticky();
        } else {
            if (this.sidebar) this.sidebar.style.display = 'none';
        }
    }

    setupDynamicSticky() {
        document.documentElement.style.setProperty('--sidebar-top', '180px');

        window.addEventListener('scroll', () => {
            if (!this.ticking) {
                window.requestAnimationFrame(() => {
                    this.updateStickyPosition();
                    this.ticking = false;
                });
                this.ticking = true;
            }
        });
    }

    updateStickyPosition() {
        const currentScrollY = window.pageYOffset;

        if (currentScrollY > this.lastScrollY && currentScrollY > 100) {
            document.documentElement.style.setProperty('--sidebar-top', '40px');
        } else {
            document.documentElement.style.setProperty('--sidebar-top', '180px');
        }

        this.lastScrollY = currentScrollY;
    }

    scanHeadings() {
        const h2Elements = this.articleContent.querySelectorAll('h2');

        h2Elements.forEach((heading, index) => {
            if (!heading.id) {
                heading.id = this.generateId(heading.textContent, index);
            }

            this.headings.push({
                id: heading.id,
                text: heading.textContent,
                element: heading
            });
        });
    }

    generateId(text, index) {
        const base = text
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .substring(0, 50);
        return `toc-${base}-${index}`;
    }

    generateToC() {
        const fragment = document.createDocumentFragment();

        // H1 title link (scroll to top)
        const h1 = document.querySelector('.article-hero__title');
        if (h1) {
            const h1Wrapper = document.createElement('div');
            h1Wrapper.className = 'article-toc__h1-wrapper';

            const h1Link = document.createElement('a');
            h1Link.className = 'article-toc__link article-toc__link--h1';
            h1Link.href = '#';
            h1Link.textContent = h1.textContent;
            h1Link.addEventListener('click', (e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });

            h1Wrapper.appendChild(h1Link);

            const tocNav = this.tocContainer.closest('.article-toc');
            if (tocNav) {
                tocNav.insertBefore(h1Wrapper, this.tocContainer);
            }
        }

        // Timeline container (holds line + dots + items)
        const timeline = document.createElement('div');
        timeline.className = 'article-toc__timeline';

        // Background line (light pink, full height)
        const line = document.createElement('div');
        line.className = 'article-toc__line';
        timeline.appendChild(line);

        // Progress fill line (pink, grows to active item)
        const progress = document.createElement('div');
        progress.className = 'article-toc__progress';
        timeline.appendChild(progress);
        this.progressLine = progress;

        // Generate items with dots
        this.headings.forEach((heading) => {
            const item = document.createElement('div');
            item.className = 'article-toc__item';
            item.dataset.targetId = heading.id;

            const dot = document.createElement('span');
            dot.className = 'article-toc__dot';
            item.appendChild(dot);
            this.dots.push(dot);

            const link = document.createElement('a');
            link.className = 'article-toc__link';
            link.href = `#${heading.id}`;
            link.textContent = heading.text;
            link.dataset.target = heading.id;
            link.addEventListener('click', (e) => this.handleLinkClick(e, heading.id));
            item.appendChild(link);

            timeline.appendChild(item);
        });

        fragment.appendChild(timeline);
        this.tocContainer.appendChild(fragment);
        this.timelineContainer = timeline;
    }

    positionDots() {
        // Dots are position:absolute against .article-toc__timeline (not their item).
        // Calculate each dot's top so it vertically centers on its item.
        const timelineRect = this.timelineContainer.getBoundingClientRect();
        const items = this.timelineContainer.querySelectorAll('.article-toc__item');

        items.forEach((item, i) => {
            if (!this.dots[i]) return;
            const itemRect = item.getBoundingClientRect();
            const itemCenterY = itemRect.top + itemRect.height / 2 - timelineRect.top;
            const dotSize = this.dots[i].offsetHeight || 8;
            this.dots[i].style.top = `${itemCenterY - dotSize / 2}px`;
        });

        // Reposition on resize
        window.addEventListener('resize', () => {
            const tRect = this.timelineContainer.getBoundingClientRect();
            const allItems = this.timelineContainer.querySelectorAll('.article-toc__item');
            allItems.forEach((item, i) => {
                if (!this.dots[i]) return;
                const iRect = item.getBoundingClientRect();
                const centerY = iRect.top + iRect.height / 2 - tRect.top;
                const dSize = this.dots[i].offsetHeight || 8;
                this.dots[i].style.top = `${centerY - dSize / 2}px`;
            });
        });
    }

    handleLinkClick(e, targetId) {
        e.preventDefault();
        this.isScrolling = true;
        history.pushState(null, null, `#${targetId}`);
        this.updateActiveState(targetId);

        const targetElement = document.getElementById(targetId);
        if (targetElement) {
            const currentOffset = parseInt(
                getComputedStyle(document.documentElement).getPropertyValue('--sidebar-top') || '100'
            );
            const offsetPosition =
                targetElement.getBoundingClientRect().top + window.pageYOffset - currentOffset - 20;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });

            setTimeout(() => {
                this.isScrolling = false;
            }, 800);
        }
    }

    setupScrollSpy() {
        const options = {
            root: null,
            rootMargin: '-10% 0px -60% 0px',
            threshold: 0
        };

        const observer = new IntersectionObserver((entries) => {
            if (this.isScrolling) return;

            const visibleEntry = entries.find((entry) => entry.isIntersecting);
            if (visibleEntry) {
                this.updateActiveState(visibleEntry.target.id);
            }
        }, options);

        this.headings.forEach((heading) => {
            if (heading.element) {
                observer.observe(heading.element);
            }
        });
    }

    updateActiveState(targetId) {
        // Find index of active heading
        const activeIndex = this.headings.findIndex((h) => h.id === targetId);
        if (activeIndex === -1) return;

        // Reset all items
        const allItems = this.timelineContainer.querySelectorAll('.article-toc__item');
        allItems.forEach((item) => item.classList.remove('active'));

        const allLinks = this.timelineContainer.querySelectorAll('.article-toc__link');
        allLinks.forEach((link) => link.classList.remove('active'));

        const allDots = this.timelineContainer.querySelectorAll('.article-toc__dot');
        allDots.forEach((dot) => dot.classList.remove('active'));

        // Activate current
        allItems[activeIndex].classList.add('active');
        allItems[activeIndex].querySelector('.article-toc__link').classList.add('active');
        this.dots[activeIndex].classList.add('active');

        // Update progress fill height
        this.updateProgressFill(activeIndex);

        // Keep active item in view
        const activeLink = allItems[activeIndex].querySelector('.article-toc__link');
        this.scrollToActiveItem(activeLink);
    }

    updateProgressFill(activeIndex) {
        if (!this.progressLine || !this.dots[activeIndex]) return;

        const dot = this.dots[activeIndex];
        const timelineRect = this.timelineContainer.getBoundingClientRect();
        const dotRect = dot.getBoundingClientRect();

        // Progress height = from top of timeline to center of active dot
        const progressHeight = dotRect.top - timelineRect.top + dot.offsetHeight / 2;
        this.progressLine.style.height = `${Math.max(0, progressHeight)}px`;
    }

    scrollToActiveItem(activeLink) {
        if (!activeLink || !this.tocContainer) return;

        const containerRect = this.tocContainer.getBoundingClientRect();
        const linkRect = activeLink.getBoundingClientRect();

        if (linkRect.top < containerRect.top || linkRect.bottom > containerRect.bottom) {
            activeLink.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest'
            });
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new ArticleToC());
} else {
    new ArticleToC();
}
